<script lang="ts">
	import SbRichtext from '../components/sbRichtext.svelte';
	import BtnPrimary from '$lib/buttons/v2/btnPrimary.svelte';
	import { onMount } from 'svelte';
	import { slide } from 'svelte/transition';
	import ProductCardPrice from '../parts/productCardPrice.svelte';
	import { canUseAffirm } from '$lib/utils/affirm';
	import { ReactiveProduct } from '$lib/state/reactiveProduct.svelte';
	import type { StripeProductData } from '../types';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import type { Stripe } from 'stripe';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';

	interface Props {
		content: ISbStoryData<{
			theme: 'default' | 'emphasis-highlight' | 'emphasis-no-highlight';
			label?: string;
			emphasis_text: string;
			price_description?: string;
			description: ISbRichtext;
			below_button_text: ISbRichtext;
			stripe_product: StripeProductData;
			reference_stripe_product?: StripeProductData;
			price_suffix?: string;
			use_affirm: boolean;
			cta_label: string;
			cta_title?: string;
			terms_of_service_text: string;
			after_submit_text: string;
			access_code: string;
			is_trial: boolean;
			stripeShippingData: Stripe.ShippingRate;
			shipping_id: string;
			shipping_id_stage: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let selected = $state(false);

	let data = $derived(
		new ReactiveProduct(content.stripe_product.priceLookupKey, content.stripe_product.data)
	);

	let reference = $derived(
		content.reference_stripe_product?.priceLookupKey
			? new ReactiveProduct(
					content.reference_stripe_product.priceLookupKey,
					content.reference_stripe_product.data
				)
			: null
	);

	onMount(() => {
		const handlePageShow = (event: PageTransitionEvent) => {
			// the page was restored from cache
			// so `onMount` and `page.subscribe` won't fire
			if (event.persisted) {
				selected = false;
			}
		};

		window.addEventListener('pageshow', handlePageShow);

		return () => {
			window.removeEventListener('pageshow', handlePageShow);
			data.unsubscribe();
			reference?.unsubscribe();
		};
	});
</script>

{#if !content.use_affirm || canUseAffirm(data.price, data.promotionCode)}
	<article
		use:editable={content}
		class="flex w-full flex-col justify-between rounded-2xl p-4 text-brown-1 lg:w-[450px] lg:p-6"
		class:bg-brown-6={content.theme === 'default'}
		class:border-4={content.theme === 'emphasis-highlight'}
		class:border-green-3={content.theme === 'emphasis-highlight'}
		class:border={content.theme === 'emphasis-no-highlight'}
		class:border-stone-2={content.theme === 'emphasis-no-highlight'}
	>
		<div>
			{#if content.theme === 'emphasis-highlight' && content.emphasis_text.length > 0}
				<div class="flex justify-between">
					<div class="flex h-6 items-center rounded-[4px] bg-green-5 px-2">
						<p class=" text-[14px]/[140%] font-semibold uppercase text-green-1">
							{content.emphasis_text}
						</p>
					</div>
				</div>
			{/if}

			<ProductCardPrice
				title={content.label}
				price={data.price}
				reference={reference?.price}
				promoCode={data.promotionCode}
				isAffirm={content.use_affirm}
				customDescription={content.price_description}
				suffix={content.price_suffix}
				isTrial={content.is_trial}
			/>

			<div class="product-description text-[16px]/[140%]">
				<SbRichtext text={content.description} />
			</div>

			{#if checkoutSession.lastError}
				<p class="pt-2 text-error-red-1" transition:slide>
					{checkoutSession.lastError}
				</p>
			{/if}
		</div>
		<div>
			<BtnPrimary
				theme={content.theme === 'emphasis-no-highlight' ? 'transparent-green-border' : 'light'}
				class="mt-6 w-full"
				size="large"
				title={content.cta_title}
				disabled={selected}
				onclick={async () => {
					selected = true;

					await checkoutSession.select(content.stripe_product, {
						accessCode: content.access_code,
						termsOfService: content.terms_of_service_text,
						afterSubmitText: content.after_submit_text,
						isTrial: content.is_trial,
						shippingId: content.stripeShippingData?.id
					});

					await checkoutSession.submit();
				}}
			>
				<span class="font-normal">{selected ? 'Processing...' : content.cta_label}</span>
			</BtnPrimary>
			{#if content.below_button_text}
				<div class="mt-3 text-center text-[14px]/[140%]">
					<SbRichtext text={content.below_button_text} />
				</div>
			{/if}
		</div>
	</article>
{/if}

<style lang="scss">
	.product-description {
		:global {
			ul {
				list-style: disc;

				li {
					margin-left: 1rem;
				}
			}
		}
	}
</style>
